import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Card,
} from "@mui/material";
import DeleteBlack_Image from "assets/images/delete-black.svg";
import DeleteWhite_Image from "assets/images/delete-white.svg";
import LockedBlack_Image from "assets/images/locked_black.svg";
import LockedWhite_Image from "assets/images/locked_white.svg";
import ResizeHandle_Image from "assets/images/resizehandle.svg";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";
import { AccountContext } from "context/account";
import parse from "html-react-parser";
import * as html2canvas from "html2canvas";
import { nanoid } from "nanoid";
import ImportColorModal from "pages/de/palette/components/ImportColorModal";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import toast from "react-hot-toast";
import "react-image-crop/dist/ReactCrop.css";
import { Group, Image as KImage, Layer, Line, Rect, Text } from "react-konva";
import { Html } from "react-konva-utils";
import "react-multi-carousel/lib/styles.css";
import { useHoverDirty } from "react-use";
import api from "services/api";
import useImage from "use-image";
import SearchIcon from "@mui/icons-material/Search";
import Tooltip from "@mui/material/Tooltip";
import ColorizeIcon from "@mui/icons-material/Colorize";

export const blockPixels = 10;
export const minBlock = 8;
export const dragHeaderHeight = 20;

export const getImageRatio = (component) => {
  var ratio = component.rawheight / component.rawwidth;
  if (component.crop) ratio *= component.crop.height / component.crop.width;
  if (component.rotation == 1 || component.rotation == 3) ratio = 1 / ratio;
  return ratio;
};

export const GridLayer = ({ width, height }) => {
  var xlist = [];
  for (let x = 0; x <= width; x += blockPixels) xlist.push(x);
  var ylist = [];
  for (let y = 0; y <= height; y += blockPixels) ylist.push(y);
  return (
    <Layer>
      {xlist.map((x) => (
        <Line
          key={"X" + x.toString()}
          x={x}
          points={[0, 0, 0, height]}
          stroke="rgba(0, 0, 0, 0.05)"
          strokeWidth={1}
        />
      ))}
      {ylist.map((y) => (
        <Line
          zindex
          key={"Y" + y.toString()}
          y={y}
          points={[0, 0, width, 0]}
          stroke="rgba(0, 0, 0, 0.05)"
          strokeWidth={1}
        />
      ))}
    </Layer>
  );
};

const LockHandle = ({ component, unLockfunc, isBlack = true, yoffset = 0 }) => {
  const [img] = useImage(isBlack ? LockedBlack_Image : LockedWhite_Image);
  const hoverRef = useRef(null);
  const isHovered = useHoverDirty(hoverRef);

  useEffect(() => {
    if (isHovered) {
      hoverRef.current.getStage().container().style.cursor = "pointer";
    } else {
      hoverRef.current.getStage().container().style.cursor = "default";
    }
  }, [isHovered]);
  var imgsize = 20;
  return (
    <KImage
      ref={hoverRef}
      image={img}
      x={component.x}
      y={component.y + yoffset}
      width={imgsize}
      height={imgsize}
      onClick={(e) => unLockfunc(component, e)}
    />
  );
};

const DeleteHandle = ({ component, deleteFunc, isBlack = true, yoffset = 0 }) => {
  const [img] = useImage(isBlack ? DeleteBlack_Image : DeleteWhite_Image);
  const hoverRef = useRef(null);
  const isHovered = useHoverDirty(hoverRef);

  useEffect(() => {
    if (isHovered) {
      hoverRef.current.getStage().container().style.cursor = "pointer";
    } else {
      hoverRef.current.getStage().container().style.cursor = "default";
    }
  }, [isHovered]);

  var imgsize = 20;
  return (
    <KImage
      ref={hoverRef}
      image={img}
      x={component.x + component.width - imgsize}
      y={component.y + yoffset}
      width={imgsize}
      height={imgsize}
      onClick={(e) => deleteFunc(component, e)}
    />
  );
};

const ResizeHandle = ({ component, setPosition, cursor = "", yoffset = 0 }) => {
  const [img] = useImage(ResizeHandle_Image);
  const hoverRef = useRef(null);
  const isHovered = useHoverDirty(hoverRef);

  useEffect(() => {
    if (isHovered) {
      hoverRef.current.getStage().container().style.cursor = cursor === "" ? "nwse-resize" : cursor;
    } else {
      hoverRef.current.getStage().container().style.cursor = "default";
    }
  }, [isHovered]);

  var imgsize = 20;
  return (
    <KImage
      ref={hoverRef}
      draggable
      image={img}
      x={component.x + component.width - imgsize}
      y={component.y + component.height - imgsize + yoffset}
      width={imgsize}
      height={imgsize}
      onDragMove={(e) => {
        setPosition(
          e.target.x() + imgsize,
          e.target.y() + imgsize - yoffset,
          false,
          e.evt.shiftKey
        );
      }}
      onDragEnd={(e) => {
        setPosition(e.target.x() + imgsize, e.target.y() + imgsize - yoffset, true, e.evt.shiftKey);
      }}
      dragBoundFunc1={(p) => {
        return {
          x: Math.max(p.x, component.x + minBlock * blockPixels - imgsize),
          y: Math.max(p.y, component.y + minBlock * blockPixels - imgsize),
        };
      }}
    />
  );
};

export const MBImage = ({
  component,
  updateComponent,
  selectedComponentId,
  setSelectedComponentId,
  unLockfunc,
  deleteFunc,
  setShowDialog,
  permission,
  setContextMenuComponent,
  exportImgRef = null,
}) => {
  const [img] = useImage(component.src, "anonymous", "origin");
  const [entered, setEntered] = useState(false);

  var locked = "lock" in component && component.lock === 1;
  var showcaption = component.caption?.length > 0;
  var selected = selectedComponentId?.includes(component.id);

  const setPosition = (x, y, dragEnd = false, shiftKey = false) => {
    //console.log("setPosition", component.x, component.y, x, y);
    component.freesize = shiftKey;
    var newwidth = Math.max(x - component.x, minBlock * blockPixels - 2);
    var newheight = component.freesize
      ? Math.max(y - component.y, minBlock * blockPixels - 2)
      : newwidth * getImageRatio(component);
    var updateObj = { ...component, width: newwidth, height: newheight };
    //console.log("setPosition2", updateObj.x, updateObj.y, updateObj.width, updateObj.height);
    updateComponent(updateObj, 2, dragEnd);
  };

  //console.log('component', component)
  var crop = undefined;
  if (component.crop)
    crop = {
      x: (component.rawwidth * component.crop.x) / 100,
      y: (component.rawheight * component.crop.y) / 100,
      width: (component.rawwidth * component.crop.width) / 100,
      height: (component.rawheight * component.crop.height) / 100,
    };
  const rotation = component.rotation ?? 0;
  return (
    <Group
      ref={(node) => {
        if (exportImgRef) exportImgRef.current[component.id] = node;
      }}
      onMouseEnter={() => {
        setEntered(true);
      }}
      onMouseLeave={() => {
        setEntered(false);
      }}
      onClick={(e) => {
        if (e.evt.button === 2) return;
        if (e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey) {
          if (!selectedComponentId.includes(component.id))
            setSelectedComponentId([...selectedComponentId, component.id]);
          else setSelectedComponentId(selectedComponentId.filter((cid) => cid !== component.id));
        } else setSelectedComponentId([component.id]);
      }}
      onDblClick={(e) => {
        if (e.evt.button === 2) return;
        window.open(component.src, "_blank");
      }}
      onContextMenu={(e) => {
        if (!selectedComponentId.includes(component.id)) setSelectedComponentId([component.id]);
        if (permission > 1) setContextMenuComponent({ x: e.evt.offsetX, y: e.evt.offsetY });
        e.cancelBubble = true;
        e.evt.preventDefault();
      }}
    >
      <KImage
        draggable={!locked && permission > 1}
        image={img}
        crop={crop}
        rotation={rotation * 90}
        x={component.x}
        y={component.y}
        width={rotation == 0 || rotation == 2 ? component.width : component.height}
        height={rotation == 0 || rotation == 2 ? component.height : component.width}
        offsetX={rotation < 2 ? 0 : rotation == 2 ? component.width : component.height}
        offsetY={
          rotation == 0 || rotation == 3 ? 0 : rotation == 1 ? component.width : component.height
        }
        onDragMove={(e) => {
          updateComponent({ ...component, x: e.target.x(), y: e.target.y() }, 2, false, true);
        }}
        onDragEnd={(e) => {
          if (!selectedComponentId.includes(component.id)) setSelectedComponentId([]);
          updateComponent({ ...component, x: e.target.x(), y: e.target.y() }, 2, true, true);
        }}
      />
      {showcaption && (
        <>
          <Rect
            x={component.x}
            y={component.y + component.height}
            width={component.width}
            height={60}
            fill={component.bgcolor === "" ? undefined : component.bgcolor ?? "#FFFFFF"}
            draggable={!locked && permission > 1}
            onDragMove={(e) => {
              updateComponent(
                { ...component, x: e.target.x(), y: e.target.y() - component.height },
                2,
                false,
                true
              );
            }}
            onDragEnd={(e) => {
              if (!selectedComponentId.includes(component.id)) setSelectedComponentId([]);
              updateComponent(
                {
                  ...component,
                  x: e.target.x(),
                  y: e.target.y() - component.height,
                },
                2,
                true,
                true
              );
            }}
          />
          <Text
            x={component.x + 3}
            y={component.y + component.height + 3}
            width={component.width - 6}
            height={60 - 6}
            text={component.caption}
            fill="#000000"
            fontSize={16}
            ellipsis={true}
            verticalAlign="middle"
            align="center"
            draggable={!locked && permission > 1}
            onDragMove={(e) => {
              updateComponent(
                { ...component, x: e.target.x() - 3, y: e.target.y() - component.height - 3 },
                2,
                false,
                true
              );
            }}
            onDragEnd={(e) => {
              if (!selectedComponentId.includes(component.id)) setSelectedComponentId([]);
              updateComponent(
                {
                  ...component,
                  x: e.target.x() - 3,
                  y: e.target.y() - component.height - 3,
                },
                2,
                true,
                true
              );
            }}
          />
        </>
      )}
      <Line
        x={component.x - 1}
        y={component.y - 1}
        points={[
          0,
          0,
          0,
          component.height + 2 + (showcaption ? 60 : 0),
          component.width + 2,
          component.height + 2 + (showcaption ? 60 : 0),
          component.width + 2,
          0,
          0,
          0,
        ]}
        stroke={selected ? "rgba(0, 0, 0, 1)" : "rgba(0, 0, 0, 0.2)"}
        strokeWidth={selected ? 2 : 1}
      />
      {entered && !locked && permission > 1 && (
        <ResizeHandle
          component={component}
          setPosition={setPosition}
          yoffset={showcaption ? 60 : 0}
        />
      )}
      {entered && permission > 1 && <DeleteHandle component={component} deleteFunc={deleteFunc} />}
      {entered && locked && permission > 1 && (
        <LockHandle component={component} unLockfunc={unLockfunc} />
      )}
    </Group>
  );
};

export const MBColor = ({
  component,
  updateComponent,
  selectedComponentId,
  setSelectedComponentId,
  unLockfunc,
  deleteFunc,
  setShowDialog,
  permission,
  setContextMenuComponent,
  exportImgRef = null,
}) => {
  const [entered, setEntered] = useState(false);

  var showcaption = component.caption?.length > 0;
  var locked = "lock" in component && component.lock === 1;
  var selected = selectedComponentId?.includes(component.id);

  const setPosition = (x, y, dragEnd = false) => {
    var newwidth = Math.max(x - component.x, minBlock * blockPixels - 2);
    var newheight = Math.max(y - component.y, minBlock * blockPixels - 2);
    var updateObj = { ...component, width: newwidth, height: newheight };
    updateComponent(updateObj, 2, dragEnd);
  };

  //console.log('component', component)

  var r = parseInt(component.color.substring(1, 3), 16);
  var g = parseInt(component.color.substring(3, 5), 16);
  var b = parseInt(component.color.substring(5, 7), 16);
  const brightness = Math.round((r * 299 + g * 587 + b * 114) / 1000);
  const isBlack = brightness > 125;
  const textColour = isBlack ? "#000000" : "#FFFFFF";

  return (
    <Group
      ref={(node) => {
        if (exportImgRef) exportImgRef.current[component.id] = node;
      }}
      onMouseEnter={() => {
        setEntered(true);
      }}
      onMouseLeave={() => {
        setEntered(false);
      }}
      onClick={(e) => {
        if (e.evt.button === 2) return;
        if (e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey) {
          if (!selectedComponentId.includes(component.id))
            setSelectedComponentId([...selectedComponentId, component.id]);
          else setSelectedComponentId(selectedComponentId.filter((cid) => cid !== component.id));
        } else setSelectedComponentId([component.id]);
      }}
      onDblClick={(e) => {
        if (e.evt.button === 2) return;
        if (permission > 1) setShowDialog(true);
      }}
      onContextMenu={(e) => {
        if (!selectedComponentId.includes(component.id)) setSelectedComponentId([component.id]);
        if (permission > 1) setContextMenuComponent({ x: e.evt.offsetX, y: e.evt.offsetY });
        e.cancelBubble = true;
        e.evt.preventDefault();
      }}
    >
      <Rect
        draggable={!locked && permission > 1}
        x={component.x}
        y={component.y}
        width={component.width}
        height={component.height}
        fill={component.color}
        onDragMove={(e) => {
          updateComponent({ ...component, x: e.target.x(), y: e.target.y() }, 2, false, true);
        }}
        onDragEnd={(e) => {
          if (!selectedComponentId.includes(component.id)) setSelectedComponentId([]);
          updateComponent({ ...component, x: e.target.x(), y: e.target.y() }, 2, true, true);
        }}
      />
      {showcaption && (
        <>
          <Rect
            x={component.x}
            y={component.y + component.height}
            width={component.width}
            height={60}
            fill={component.bgcolor === "" ? undefined : component.bgcolor ?? "#FFFFFF"}
            draggable={!locked && permission > 1}
            onDragMove={(e) => {
              updateComponent(
                { ...component, x: e.target.x(), y: e.target.y() - component.height },
                2,
                false,
                true
              );
            }}
            onDragEnd={(e) => {
              if (!selectedComponentId.includes(component.id)) setSelectedComponentId([]);
              updateComponent(
                {
                  ...component,
                  x: e.target.x(),
                  y: e.target.y() - component.height,
                },
                2,
                true,
                true
              );
            }}
          />
          <Text
            x={component.x + 3}
            y={component.y + component.height + 3}
            width={component.width - 6}
            height={60 - 6}
            text={component.caption}
            fill="#000000"
            fontSize={16}
            ellipsis={true}
            verticalAlign="middle"
            align="center"
            draggable={!locked && permission > 1}
            onDragMove={(e) => {
              updateComponent(
                { ...component, x: e.target.x() - 3, y: e.target.y() - component.height - 3 },
                2,
                false,
                true
              );
            }}
            onDragEnd={(e) => {
              if (!selectedComponentId.includes(component.id)) setSelectedComponentId([]);
              updateComponent(
                {
                  ...component,
                  x: e.target.x() - 3,
                  y: e.target.y() - component.height - 3,
                },
                2,
                true,
                true
              );
            }}
          />
        </>
      )}
      <Text
        x={component.x + Math.min(30, component.width / 2 - 32)}
        y={component.y + Math.min(20, component.height / 2 - 7)}
        text={component.color}
        fill={textColour}
        fontSize={16}
        fontStyle="bold"
      />
      <Line
        x={component.x - 1}
        y={component.y - 1}
        points={[
          0,
          0,
          0,
          component.height + 2 + (showcaption ? 60 : 0),
          component.width + 2,
          component.height + 2 + (showcaption ? 60 : 0),
          component.width + 2,
          0,
          0,
          0,
        ]}
        stroke={selected ? "rgba(0, 0, 0, 1)" : "rgba(0, 0, 0, 0.2)"}
        strokeWidth={selected ? 2 : 1}
      />
      {entered && !locked && permission > 1 && (
        <ResizeHandle
          component={component}
          setPosition={setPosition}
          yoffset={showcaption ? 60 : 0}
        />
      )}
      {entered && permission > 1 && (
        <DeleteHandle component={component} deleteFunc={deleteFunc} isBlack={isBlack} />
      )}
      {entered && locked && permission > 1 && (
        <LockHandle component={component} unLockfunc={unLockfunc} isBlack={isBlack} />
      )}
    </Group>
  );
};

export const MBText = ({
  component,
  updateComponent,
  selectedComponentId,
  setSelectedComponentId,
  unLockfunc,
  deleteFunc,
  setShowDialog,
  permission,
  setContextMenuComponent,
  exportImgRef = null,
}) => {
  const [entered, setEntered] = useState(false);
  const [width, setWidth] = useState(component.width);
  const [height, setHeight] = useState(component.height - 46);
  const [textMode, setTextMode] = useState(0);
  const [textImage, setTextImg] = useState(null);
  const elementRef = useRef(null);

  const rotation = component.rotation ?? 0;
  const rotationKeep = rotation === 0 || rotation === 2;

  const adjustContentHeight = () => {
    if (elementRef.current === null || elementRef.current?.clientHeight === undefined) {
      setTimeout(adjustContentHeight, 10);
    } else {
      //console.log('adjustContentHeight', elementRef.current?.clientHeight)
      setWidth(elementRef.current?.clientWidth);
      setHeight(elementRef.current?.clientHeight);
      setTextMode(1);
      var newwidth = rotationKeep ? component.width : component.height;
      var newheight =
        Math.max(minBlock, Math.round((elementRef.current?.clientHeight + 46) / blockPixels)) *
          blockPixels -
        2;
      var finalwidth = rotationKeep ? newwidth : newheight;
      var finalheight = rotationKeep ? newheight : newwidth;
      if (updateComponent && (component.width !== finalwidth || component.height !== finalheight)) {
        var newcomponent = { ...component, width: finalwidth, height: finalheight };
        //console.log('newcomponent', newcomponent)
        updateComponent(newcomponent);
      }
    }
  };

  useEffect(() => {
    adjustContentHeight();
  }, []);

  useEffect(() => {
    if (textMode == 0) {
      adjustContentHeight();
    } else if (textMode == 2) {
      setTextMode(0);
    }
  }, [rotationKeep ? component.width : component.height, component.content]);

  useEffect(() => {
    if (textMode == 0) {
      adjustContentHeight();
      setTextImg(null);
    } else if (textMode == 1) {
      if (height > 0) {
        var scale = 2.5; //定义任意放大倍数 支持小数
        elementRef.current.style.fontFeatureSettings = '"liga" 0';
        elementRef.current.style.width = width + "px";
        elementRef.current.style.height = height + "px";
        elementRef.current.style.position = "absolute";
        elementRef.current.style.top = "0";
        elementRef.current.style.left = "0";
        elementRef.current.style.transform = "";
        var canvas = document.createElement("canvas");
        canvas.width = Math.floor(width * scale * window.devicePixelRatio);
        canvas.height = Math.floor(height * scale * window.devicePixelRatio);
        canvas.style.width = width + "px";
        canvas.style.height = height + "px";
        var context = canvas.getContext("2d");
        context.scale(scale, scale);
        context.mozImageSmoothingEnabled = false;
        context.webkitImageSmoothingEnabled = false;
        context.msImageSmoothingEnabled = false;
        context.imageSmoothingEnabled = false;
        let opts = {
          backgroundColor: null,
          canvas: canvas,
          logging: false, //日志开关，便于查看html2canvas的内部执行流程
          width: width, //dom 原始宽度
          height: height,
          useCORS: true, // 【重要】开启跨域配置
          allowTaint: true,
        };
        //.log("width", width, "height", height);
        html2canvas(elementRef.current, opts).then((canvas) => {
          setTextImg(canvas);
          setTextMode(2);
          //console.log("canvas", canvas);
        });
      }
    }
  }, [textMode]);

  var locked = "lock" in component && component.lock === 1;
  var selected = selectedComponentId?.includes(component.id);

  const setPosition = (x, y, dragEnd = false) => {
    var newwidth = Math.max(x - component.x, minBlock * blockPixels - 2);
    var newheight = Math.max(y - component.y, minBlock * blockPixels - 2);
    if (dragEnd) {
      //updateComponent({ ...component, width: newwidth, height: newheight });
      setTextMode(0);
    } else {
      setTextMode(3);
      if (rotationKeep) {
        component.width = newwidth;
        setWidth(component.width);
      } else {
        component.height = newheight;
        setHeight(component.height);
      }
    }
  };

  //console.log('rotation', rotation, 'width', width, 'height', height, 'textImage', textImage)
  return (
    <Group
      ref={(node) => {
        if (exportImgRef) exportImgRef.current[component.id] = node;
      }}
      onMouseEnter={() => {
        setEntered(true);
      }}
      onMouseLeave={() => {
        setEntered(false);
      }}
      onClick={(e) => {
        if (e.evt.button === 2) return;
        if (e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey) {
          if (!selectedComponentId.includes(component.id))
            setSelectedComponentId([...selectedComponentId, component.id]);
          else setSelectedComponentId(selectedComponentId.filter((cid) => cid !== component.id));
        } else setSelectedComponentId([component.id]);
      }}
      onDblClick={(e) => {
        if (e.evt.button === 2) return;
        if (permission > 1) setShowDialog(true);
      }}
      onContextMenu={(e) => {
        if (!selectedComponentId.includes(component.id)) setSelectedComponentId([component.id]);
        if (permission > 1) setContextMenuComponent({ x: e.evt.offsetX, y: e.evt.offsetY });
        e.cancelBubble = true;
        e.evt.preventDefault();
      }}
    >
      <Rect
        draggable={!locked && permission > 1}
        x={component.x}
        y={component.y}
        width={component.width}
        height={component.height}
        fill={component.bgcolor === "" ? undefined : component.bgcolor ?? "#FFFFFF"}
        onDragMove={(e) => {
          updateComponent({ ...component, x: e.target.x(), y: e.target.y() }, 2, false, true);
        }}
        onDragEnd={(e) => {
          if (!selectedComponentId.includes(component.id)) setSelectedComponentId([]);
          updateComponent({ ...component, x: e.target.x(), y: e.target.y() }, 2, true, true);
        }}
      ></Rect>
      {textMode === 2 && textImage && (
        <KImage
          listening={false}
          image={textImage}
          rotation={rotation * 90}
          x={component.x + 16}
          y={component.y + 16}
          width={width}
          height={height}
          offsetX={rotation < 2 ? 0 : width}
          offsetY={rotation == 0 || rotation == 3 ? 0 : height + 10}
        />
      )}
      <Line
        x={component.x - 1}
        y={component.y - 1}
        points={[
          0,
          0,
          0,
          component.height + 2,
          component.width + 2,
          component.height + 2,
          component.width + 2,
          0,
          0,
          0,
        ]}
        stroke={selected ? "rgba(0, 0, 0, 1)" : "rgba(0, 0, 0, 0.2)"}
        strokeWidth={selected ? 2 : 1}
      />
      {textMode !== 2 && (
        <Html
          //(component.width - component.height) / 2
          groupProps={{
            x: component.x + 16,
            y: component.y + 16,
          }}
          divProps={{
            style: {
              opacity: 1,
              pointerEvents: "none",
              //width: Math.max(component.width, component.height),
              //height: Math.max(component.width, component.height),
            },
          }}
        >
          <div
            style={{
              width: `${(rotationKeep ? component.width : component.height) - 32}px`,
              height: `${(rotationKeep ? component.height : component.width) - 32}px`,
              overflow: "hidden",
            }}
          >
            <div
              ref={elementRef}
              style={{
                whiteSpace: "normal",
                overflowWrap: "break-word",
                wordWrap: "break-word",
                lineHeight: 1.15,
                //transform: `rotate(${rotation === 21 ? rotation * 90 : 0}deg)`,
              }}
            >
              {parse(component.content)}
            </div>
          </div>
        </Html>
      )}
      {entered && !locked && permission > 1 && (
        <ResizeHandle
          component={component}
          setPosition={setPosition}
          cursor={rotationKeep ? "ew-resize" : "ns-resize"}
        />
      )}
      {entered && permission > 1 && <DeleteHandle component={component} deleteFunc={deleteFunc} />}
      {entered && permission > 1 && locked && (
        <LockHandle component={component} unLockfunc={unLockfunc} />
      )}
    </Group>
  );
};

export const MBVideo = ({
  component,
  updateComponent,
  selectedComponentId,
  setSelectedComponentId,
  unLockfunc,
  deleteFunc,
  permission,
  setContextMenuComponent,
  exportFlag = false,
  exportImgRef = null,
}) => {
  const [entered, setEntered] = useState(false);
  const [exportImg, setExportImg] = useState(null);

  var locked = "lock" in component && component.lock === 1;
  var selected = selectedComponentId?.includes(component.id);
  var bgcolor = component.bgcolor === "" ? undefined : component.bgcolor ?? "#FFFFFF";

  const setPosition = (x, y, dragEnd = false) => {
    var newwidth = Math.max(x - component.x, 21 * blockPixels - 2);
    var newheight = Math.max(y - component.y, 13 * blockPixels - 2);
    var updateObj = { ...component, width: newwidth, height: newheight };
    updateComponent(updateObj, 2, dragEnd);
  };

  const loadExportImg = async () => {
    const response = await api.uploads.downloadurl({ url: component.imgurl });
    if (response.data.contenttype.startsWith("image/")) {
      var imgstr = "data:" + response.data.contenttype + ";base64," + response.data.base64;
      //console.log(imgstr)
      setExportImg(imgstr);
    }
  };

  const [exportImg2] = useImage(exportImg);

  useEffect(() => {
    if (exportFlag && component?.imgurl) {
      loadExportImg();
    }
  }, [component?.imgurl, exportFlag]);

  return (
    <>
      <Group
        ref={(node) => {
          if (exportImgRef) exportImgRef.current[component.id] = node;
        }}
        onMouseEnter={() => {
          setEntered(true);
        }}
        onMouseLeave={() => {
          setEntered(false);
        }}
        onClick={(e) => {
          if (e.evt.button === 2) return;
          if (e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey) {
            if (!selectedComponentId.includes(component.id))
              setSelectedComponentId([...selectedComponentId, component.id]);
            else setSelectedComponentId(selectedComponentId.filter((cid) => cid !== component.id));
          } else setSelectedComponentId([component.id]);
        }}
        onDblClick={(e) => {}}
        onContextMenu={(e) => {
          if (!selectedComponentId.includes(component.id)) setSelectedComponentId([component.id]);
          if (permission > 1) setContextMenuComponent({ x: e.evt.offsetX, y: e.evt.offsetY });
          e.cancelBubble = true;
          e.evt.preventDefault();
        }}
      >
        {!exportFlag && (
          <Rect
            x={component.x}
            y={component.y - dragHeaderHeight}
            width={component.width}
            height={dragHeaderHeight}
            fill={entered ? bgcolor : "transparent"}
          />
        )}
        {!exportFlag && (
          <Text
            draggable={!locked && permission > 1}
            onDragMove={(e) => {
              updateComponent(
                { ...component, x: e.target.x(), y: e.target.y() + dragHeaderHeight },
                2,
                false,
                true
              );
            }}
            onDragEnd={(e) => {
              if (!selectedComponentId.includes(component.id)) setSelectedComponentId([]);
              updateComponent(
                { ...component, x: e.target.x(), y: e.target.y() + dragHeaderHeight },
                2,
                true,
                true
              );
            }}
            x={component.x}
            y={component.y - dragHeaderHeight}
            width={component.width}
            height={dragHeaderHeight}
            fill="#000000"
            text={entered ? "Drag me" : ""}
            fontSize={17}
            ellipsis={true}
            wrap={"none"}
            align="center"
            verticalAlign="middle"
          />
        )}
        <Rect
          draggable={!locked && permission > 1}
          onDragMove={(e) => {
            updateComponent({ ...component, x: e.target.x(), y: e.target.y() }, 2, false, true);
          }}
          onDragEnd={(e) => {
            if (!selectedComponentId.includes(component.id)) setSelectedComponentId([]);
            updateComponent({ ...component, x: e.target.x(), y: e.target.y() }, 2, true, true);
          }}
          x={component.x}
          y={component.y}
          width={component.width}
          height={component.height + 60}
          fill={bgcolor}
        ></Rect>
        <Text
          x={component.x + 12}
          y={component.y + component.height + 18}
          width={component.width - 24}
          text={component.caption}
          fill="#000000"
          fontSize={17}
          ellipsis={true}
          wrap={"none"}
          onClick={() => window.open(component.url, "_blank")}
          textDecoration={"underline"}
          onMouseEnter={(e) => {
            e.target.getStage().container().style.cursor = "pointer";
          }}
          onMouseLeave={(e) => {
            e.target.getStage().container().style.cursor = "default";
          }}
        />
        <Line
          x={component.x - 1}
          y={component.y - 1 - (entered ? dragHeaderHeight : 0)}
          points={[
            0,
            0,
            0,
            component.height + 62 + (entered ? dragHeaderHeight : 0),
            component.width + 2,
            component.height + 62 + (entered ? dragHeaderHeight : 0),
            component.width + 2,
            0,
            0,
            0,
          ]}
          stroke={selected ? "rgba(0, 0, 0, 1)" : "rgba(0, 0, 0, 0.2)"}
          strokeWidth={selected ? 2 : 1}
        />
        {exportFlag && (
          <KImage
            crop={{
              x: 0,
              y: 360 * 0.125,
              width: 480,
              height: 360 * (1 - 2 * 0.125),
            }}
            image={exportImg2}
            x={component.x}
            y={component.y}
            width={component.width}
            height={component.height}
          />
        )}
        {entered && !locked && permission > 1 && (
          <ResizeHandle component={component} setPosition={setPosition} yoffset={60} />
        )}
        {entered && permission > 1 && (
          <DeleteHandle component={component} deleteFunc={deleteFunc} yoffset={component.height} />
        )}
        {entered && locked && permission > 1 && (
          <LockHandle component={component} unLockfunc={unLockfunc} yoffset={component.height} />
        )}
      </Group>
      {!exportFlag && (
        <Html groupProps={{ x: component.x, y: component.y }} divProps={{ style: { opacity: 1 } }}>
          <div
            style={{
              width: `${component.width}px`,
              height: `${component.height}px`,
              overflow: "hidden",
            }}
          >
            <iframe
              width={component.width}
              height={component.height}
              src={"https://www.youtube.com/embed/" + component.youtube_id}
              title="YouTube video player"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowfullscreen
            ></iframe>
          </div>
        </Html>
      )}
    </>
  );
};

export const MBMusic = ({
  component,
  updateComponent,
  selectedComponentId,
  setSelectedComponentId,
  unLockfunc,
  deleteFunc,
  permission,
  setContextMenuComponent,
  exportFlag = false,
  exportImgRef = null,
}) => {
  const [entered, setEntered] = useState(false);

  var locked = "lock" in component && component.lock === 1;
  var selected = selectedComponentId?.includes(component.id);
  var bgcolor = component.bgcolor === "" ? undefined : component.bgcolor ?? "#FFFFFF";

  const setPosition = (x, y, dragEnd = false) => {
    var newwidth = Math.max(x - component.x, 21 * blockPixels - 2);
    var newheight = y - component.y;
    // spotify height 80, 152, 232, 352
    if (newheight > 358) newheight = 358;
    else if (newheight > 238) newheight = 238;
    else if (newheight > 158) newheight = 158;
    else newheight = 88;
    var updateObj = { ...component, width: newwidth, height: newheight };
    updateComponent(updateObj, 2, dragEnd);
  };

  const [img] = useImage(component.imgurl, "anonymous", "origin");
  return (
    <>
      <Group
        ref={(node) => {
          if (exportImgRef) exportImgRef.current[component.id] = node;
        }}
        onMouseEnter={() => {
          setEntered(true);
        }}
        onMouseLeave={() => {
          setEntered(false);
        }}
        onClick={(e) => {
          if (e.evt.button === 2) return;
          if (e.evt.shiftKey || e.evt.metaKey || e.evt.ctrlKey) {
            if (!selectedComponentId.includes(component.id))
              setSelectedComponentId([...selectedComponentId, component.id]);
            else setSelectedComponentId(selectedComponentId.filter((cid) => cid !== component.id));
          } else setSelectedComponentId([component.id]);
        }}
        onDblClick={(e) => {}}
        onContextMenu={(e) => {
          if (!selectedComponentId.includes(component.id)) setSelectedComponentId([component.id]);
          if (permission > 1) setContextMenuComponent({ x: e.evt.offsetX, y: e.evt.offsetY });
          e.cancelBubble = true;
          e.evt.preventDefault();
        }}
      >
        {!exportFlag && (
          <Rect
            x={component.x}
            y={component.y - dragHeaderHeight}
            width={component.width}
            height={dragHeaderHeight}
            fill={entered ? bgcolor : "transparent"}
          />
        )}
        {!exportFlag && (
          <Text
            draggable={!locked && permission > 1}
            onDragMove={(e) => {
              updateComponent(
                { ...component, x: e.target.x(), y: e.target.y() + dragHeaderHeight },
                2,
                false,
                true
              );
            }}
            onDragEnd={(e) => {
              if (!selectedComponentId.includes(component.id)) setSelectedComponentId([]);
              updateComponent(
                { ...component, x: e.target.x(), y: e.target.y() + dragHeaderHeight },
                2,
                true,
                true
              );
            }}
            x={component.x}
            y={component.y - dragHeaderHeight}
            width={component.width}
            height={dragHeaderHeight}
            fill="#000000"
            text={entered ? "Drag me" : ""}
            fontSize={17}
            ellipsis={true}
            wrap={"none"}
            align="center"
            verticalAlign="middle"
          />
        )}
        <Rect
          draggable={!locked && permission > 1}
          onDragMove={(e) => {
            updateComponent({ ...component, x: e.target.x(), y: e.target.y() }, 2, false, true);
          }}
          onDragEnd={(e) => {
            if (!selectedComponentId.includes(component.id)) setSelectedComponentId([]);
            updateComponent({ ...component, x: e.target.x(), y: e.target.y() }, 2, true, true);
          }}
          x={component.x}
          y={component.y}
          width={component.width}
          height={component.height + 60}
          fill={bgcolor}
        ></Rect>
        <Line
          x={component.x - 1}
          y={component.y - 1 - (entered ? dragHeaderHeight : 0)}
          points={[
            0,
            0,
            0,
            component.height + 62 + (entered ? dragHeaderHeight : 0),
            component.width + 2,
            component.height + 62 + (entered ? dragHeaderHeight : 0),
            component.width + 2,
            0,
            0,
            0,
          ]}
          stroke={selected ? "rgba(0, 0, 0, 1)" : "rgba(0, 0, 0, 0.2)"}
          strokeWidth={selected ? 2 : 1}
        />
        <Text
          x={component.x + 12}
          y={component.y + component.height + 18}
          width={component.width - 24}
          text={component.caption}
          fill="#000000"
          fontSize={17}
          ellipsis={true}
          wrap={"none"}
          onClick={() => window.open(component.url, "_blank")}
          textDecoration={"underline"}
          onMouseEnter={(e) => {
            e.target.getStage().container().style.cursor = "pointer";
          }}
          onMouseLeave={(e) => {
            e.target.getStage().container().style.cursor = "default";
          }}
        />
        {exportFlag && (
          <KImage
            crop={{
              x: 0,
              y: 360 * 0.125,
              width: 480,
              height: 360 * (1 - 2 * 0.125),
            }}
            image={img}
            x={component.x}
            y={component.y}
            width={component.width}
            height={component.height}
          />
        )}
        {entered && !locked && permission > 1 && (
          <ResizeHandle component={component} setPosition={setPosition} yoffset={60} />
        )}
        {entered && permission > 1 && (
          <DeleteHandle component={component} deleteFunc={deleteFunc} yoffset={component.height} />
        )}
        {entered && locked && permission > 1 && (
          <LockHandle component={component} unLockfunc={unLockfunc} yoffset={component.height} />
        )}
      </Group>
      {!exportFlag && (
        <Html groupProps={{ x: component.x, y: component.y }} divProps={{ style: { opacity: 1 } }}>
          <div
            style={{
              width: `${component.width}px`,
              height: `${component.height}px`,
              overflow: "hidden",
            }}
          >
            <iframe
              style={{ borderRadius: "0px" }}
              src={
                "https://open.spotify.com/embed/track/" +
                component.spotify_id +
                "?utm_source=generator"
              }
              width={component.width}
              height={component.height}
              frameBorder="0"
              allowfullscreen=""
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
            ></iframe>
          </div>
        </Html>
      )}
    </>
  );
};

export const ShowColorDialog = ({
  moodboardData,
  selectedComponent,
  showColorDialog,
  setShowColorDialog,
  updateComponent,
  addComponent,
  setIsDirty,
  setSelectedComponentId,
  uploadImgPosition,
}) => {
  const [importVisible, setImportVisible] = useState(false);

  const color2Ref = useRef();
  const captionRef = useRef("");
  const [color, setColor] = useState("#000000");
  const [colorId, setColorId] = useState(selectedComponent?.colorId || null);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [data, setData] = useState([]);
  const [filteredColors, setFilteredColors] = useState([]);
  const [searchStr, setSearchStr] = useState();
  const [caption, setCaption] = useState(selectedComponent?.caption || "");

  const account = useContext(AccountContext);
  color2Ref.current = color;
  const inputRef = useCallback(async (node) => {
    if (node !== null) {
      await new Promise((res) => setTimeout(res, 50));
      //node.click();
    }
  }, []);

  useEffect(() => {
    //console.log("selectedComponent", selectedComponent);
    setColor(selectedComponent ? selectedComponent.color : "#000000");
    retrievePalette();
  }, [selectedComponent]);

  function PaperComponent(props) {
    return (
      <Draggable handle="#draggable-color-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
        <Paper {...props} />
      </Draggable>
    );
  }
  //console.log('colorRef.current', colorRef.current)

  const handleExportColor = async () => {
    const colorName = caption;
    if (!colorName) {
      return toast.error(`Caption is required to add to my Color Library!`);
    }

    if (account.id != moodboardData?.owner_id) {
      toast.error(`You don't have the permission to export the color!`);
    }

    if (
      selectedComponent?.colorId &&
      selectedComponent?.caption &&
      colorName == selectedComponent.caption
    ) {
      setConfirmVisible(true);
    } else {
      let payload = {
        name: colorName,
        season: moodboardData?.metadata?.season,
        hex: color,
        metadata: {
          year: moodboardData?.metadata?.year,
        },
      };

      try {
        const { data } = await api.palette.create(payload);
        setColorId(data?.created?.id);

        retrievePalette();
        toast.success(`${colorName} has been added successfully!`);
      } catch (err) {
        toast.error(`Color could not be added!`);
      }
    }
  };
  const handleCloseConfirm = () => {
    setConfirmVisible(false);
  };

  const handleConfirmOverwrite = async () => {
    if (!selectedComponent?.colorId) {
      return toast.error(`Could not find the color!`);
    }
    let payload = {
      name: caption,
      hex: color,
    };
    try {
      const newCreatedColor = await api.palette.update(selectedComponent?.colorId, payload);
      toast.success(`${caption} has been saved successfully!`);
      handleCloseConfirm();
      handleSave(selectedComponent?.colorId);
    } catch (err) {
      console.log(err);
      toast.error(`Color could not be saved!`);
    }
  };

  const handleSave = async (colorId) => {
    setShowColorDialog(false);

    if (selectedComponent) {
      updateComponent({
        ...selectedComponent,
        color: color,
        caption: caption,
        colorId: colorId,
      });
    } else {
      var newObj = {
        ...uploadImgPosition.current,
        id: nanoid(),
        type: "color",
        width: 160,
        height: 120,
        color: color,
        caption: caption,
        ts: new Date(),
        colorId: colorId,
        archived: uploadImgPosition.current.x < 0,
      };
      addComponent(newObj);
    }
  };

  const retrievePalette = async () => {
    try {
      const { data } = await api.palette.list({
        order: "-hex",
      });
      setData(data?.colors || []);
      setFilteredColors(data?.colors || []);
    } catch (err) {
      toast.error(`Retrieve color list failed!`);
      console.log(err);
    }
  };

  const handleSearchChange = (value) => {
    if (value) {
      let filtered = data?.filter((x) => x.name.toLowerCase().includes(value.toLowerCase()));
      setFilteredColors(filtered);
    } else {
      setFilteredColors(data);
    }
    setSearchStr(value);
  };

  const existedColorIndex = data.findIndex((x) => x.id == colorId && x.hex == color);

  return (
    <Dialog
      hideBackdrop={true}
      fullWidth
      maxWidth="xs"
      open={showColorDialog}
      onClose={() => setShowColorDialog(false)}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-color-dialog-title"
    >
      <DialogTitle id="draggable-color-dialog-title" style={{ cursor: "move" }}>
        Color Picker
        <IconButton
          aria-label="close"
          onClick={() => {
            setShowColorDialog(false);
          }}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <SuiBox
          sx={{ height: "100%", width: "100%" }}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <div style={{ width: "100%" }}>
            <SuiBox mb={1}>
              <SuiBox>
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                >
                  Caption
                </SuiTypography>
              </SuiBox>
              <SuiBox sx={{ width: "100%" }}>
                <SuiInput
                  size="small"
                  value={caption}
                  onChange={(e) => {
                    setCaption(e.target.value);
                  }}
                />
              </SuiBox>
            </SuiBox>
            <SuiBox my={1} mb={2}>
              <SuiBox lineHeight={0}>
                <SuiTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                  textTransform="capitalize"
                >
                  Color
                </SuiTypography>
              </SuiBox>
              <SuiTypography
                variant="body2"
                fontWeight="regular"
                textTransform="none"
                sx={{
                  lineHeight: 1,
                  mb: 1,
                }}
              >
                Click the color swatch below; Find the <ColorizeIcon /> to access your Color Dropper
                tool; Sample and add any color from your screen
              </SuiTypography>

              <SuiBox display="flex" justifyContent="space-between" alignItems="center">
                <input
                  ref={inputRef}
                  type="color"
                  value={color}
                  style={{
                    width: 60,
                    height: 60,
                    cursor: "pointer",
                  }}
                  onChange={(e) => {
                    color2Ref.current = e.target.value.toUpperCase();
                  }}
                  onBlur={(e) => {
                    setColor(color2Ref.current);
                    if (colorId) {
                      setColorId(null);
                    }
                  }}
                />
                <SuiTypography variant="body1">{color}</SuiTypography>
              </SuiBox>
            </SuiBox>
            <Divider />
            <SuiBox>
              <SuiBox display="flex" justifyContent="space-between" alignItems="center">
                <SuiBox>
                  <SuiTypography
                    component="label"
                    variant="caption"
                    fontWeight="bold"
                    textTransform="capitalize"
                  >
                    Import From Color Library
                  </SuiTypography>
                </SuiBox>
                <SuiBox sx={{ maxWidth: 150, p: 1 }}>
                  <SuiInput
                    placeholder="Search"
                    type="text"
                    size="small"
                    onChange={(e) => handleSearchChange(e?.target?.value)}
                    value={searchStr}
                    icon={{
                      direction: "right",
                      component: <SearchIcon />,
                    }}
                  />{" "}
                </SuiBox>
              </SuiBox>
              <SuiBox
                display="flex"
                sx={{ height: 108, overflow: "auto", pr: 1, flexWrap: "wrap" }}
              >
                {filteredColors.map((x, index) => (
                  <SuiBox key={index} mr={1} sx={{ textAlign: "center" }}>
                    <SuiBox
                      sx={{
                        cursor: "pointer",
                        width: 46,
                        height: 28,
                        backgroundColor: x?.hex || "#000000",
                        border: colorId == x.id ? `ridge 3px  #21d4fd` : `solid 1px ${"#343434"}`,
                      }}
                      onClick={() => {
                        setColor(x.hex);
                        setColorId(x.id);
                        setCaption(x.name);
                      }}
                    />
                    <Tooltip title={x?.name}>
                      <SuiTypography
                        sx={{
                          wordBreak: "break-word",
                          maxWidth: 46,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: "1",
                          WebkitBoxOrient: "vertical",
                        }}
                        variant="body2"
                      >
                        {x?.name}
                      </SuiTypography>
                    </Tooltip>
                  </SuiBox>
                ))}
              </SuiBox>
            </SuiBox>
          </div>
          {account.id == moodboardData?.owner_id ? (
            !colorId || existedColorIndex == -1 ? (
              <SuiBox display="flex" gap={0.5} alignItems="center">
                <AddIcon />
                <SuiTypography
                  color="dark"
                  variant="body1"
                  sx={{
                    textTransform: "none",
                    "&:hover": { textDecoration: "underline" },
                    cursor: "pointer",
                  }}
                  onClick={handleExportColor}
                >
                  Save to my Color Library
                </SuiTypography>{" "}
              </SuiBox>
            ) : (
              <SuiTypography
                color="dark"
                variant="body1"
                sx={{
                  textTransform: "none",
                }}
              >
                Saved in Color Library
              </SuiTypography>
            )
          ) : null}
        </SuiBox>
      </DialogContent>
      <DialogActions>
        <SuiButton onClick={() => handleSave(colorId)} color={"dark"} variant="gradient">
          {selectedComponent?.color ? "Save" : "Add"}
        </SuiButton>
      </DialogActions>
      {confirmVisible ? (
        <Dialog
          open={confirmVisible}
          onClose={handleCloseConfirm}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{"Confirm Edit"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Are you sure to update the value in Color Library? You could also change to a
              different name to export it.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <SuiButton onClick={handleCloseConfirm}>Cancel</SuiButton>

            <SuiButton onClick={handleConfirmOverwrite} autoFocus color="dark" variant="gradient">
              Update
            </SuiButton>
          </DialogActions>
        </Dialog>
      ) : null}
    </Dialog>
  );
};

export const ArchivedView = ({
  archivedComponents,
  setShowArchived,
  exportImgRef,
  areaHeight,
  bgcolor,
  setComponents,
  setIsDirty,
  setDragType,
}) => {
  const [contextMenuComponent, setContextMenuComponent] = useState(null);
  const [localComponents, setLocalComponents] = useState([]);
  const archiveWidth = 200;
  var deepEqual = require("deep-equal");

  const ComponentContextMenu = ({}) => {
    const fontSize = "14px";
    var py = "2px";
    var x = contextMenuComponent?.x ?? -10000;
    var y = contextMenuComponent?.y ?? -10000;
    //console.log('contextMenuComponent', contextMenuComponent)
    if (x > 90 + archiveWidth / 2) x -= archiveWidth / 2;
    return (
      <div style={{ position: "fixed", left: x, top: y, opacity: 1 }}>
        <Paper sx={{ width: archiveWidth / 2 }}>
          <MenuList>
            <MenuItem
              sx={{ py: py, px: 0, m: 0, width: archiveWidth / 2, minWidth: archiveWidth / 2 }}
              onContextMenu={(e) => e.preventDefault()}
              onClick={() => {
                setComponents((current) =>
                  current.filter((c) => contextMenuComponent.component.id !== c.id)
                );
                setIsDirty(true);
                setContextMenuComponent(null);
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: fontSize }}>Delete</ListItemText>
            </MenuItem>
          </MenuList>
        </Paper>
      </div>
    );
  };

  const reload = async () => {
    //console.log('archivedComponents', archivedComponents, 'localComponents', localComponents, deepEqual(archivedComponents, localComponents))
    if (!deepEqual(archivedComponents, localComponents)) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      setLocalComponents([...archivedComponents]);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  useEffect(() => {
    reload();
  }, [archivedComponents]);

  return (
    <Card
      sx={{
        pt: "2px",
        position: "sticky",
        top: "0px",
        borderLeft: "1px groove rgba(255,255,255,1)",
        borderRadius: 1,
        boxShadow: ({ boxShadows: { md } }) => md,
        marginRight: 2,
        width: archiveWidth.toString() + "px",
        minWidth: archiveWidth.toString() + "px",
        height: areaHeight + 2,
        overflow: "hidden",
      }}
      onClick={() => {
        setContextMenuComponent(null);
      }}
      onContextMenu={(e) => {
        setContextMenuComponent(null);
        e.preventDefault();
      }}
      tabIndex="2"
    >
      <ComponentContextMenu />
      <SuiBox sx={{ width: "100%" }}>
        <SuiBox
          sx={{ p: 1, backgroundColor: "#f3f3f3" }}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <SuiTypography variant="h6">My Inspo</SuiTypography>
          <IconButton
            aria-label="close"
            onClick={() => {
              setShowArchived(false);
            }}
            sx={{
              position: "absolute",
              right: 2,
              top: 2,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </SuiBox>

        <SuiBox
          sx={{
            pt: 2,
            height: areaHeight - 85,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {[...localComponents].reverse().map((component) => {
            var src = exportImgRef.current[component.id]?.toDataURL();
            return (
              <SuiBox
                key={component.id}
                sx={{ p: "4px" }}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <img
                  draggable="true"
                  width={archiveWidth - 20}
                  src={src}
                  style={{ border: "1px solid rgba(0,0,0,0.2)" }}
                  onContextMenu={(e) => {
                    setContextMenuComponent({ component: component, x: e.pageX, y: e.pageY });
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDragStart={(e) => {
                    //console.log('onDragStart', e)
                    setDragType(component.id);
                    const img = new Image();
                    img.src = src;
                    e.dataTransfer.setDragImage(img, 0, 0);
                  }}
                  onDragEnd={(e) => {
                    //console.log('onDragEnd', e)
                    setDragType(null);
                  }}
                />
              </SuiBox>
            );
          })}
        </SuiBox>
      </SuiBox>
    </Card>
  );
};
